/**
 * LiquidNumber — animated tabular numeric display.
 *
 * Smoothly interpolates between values using requestAnimationFrame.
 * Designed for big balance numbers and live tickers where you want
 * the digits to feel alive. Uses tabular numerals so the layout
 * doesn't shift as digits change.
 *
 * The animation uses a custom easing curve that overshoots slightly
 * — the same "rubber band" feel iOS uses when you scroll past the
 * end of a list. Fast enough to feel responsive, slow enough to read.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { formatUsdt } from '@/lib/format';

interface LiquidNumberProps {
  value: number | string;
  decimals?: number;
  /** Animation duration in ms */
  duration?: number;
  /** Optional prefix (e.g. '$') */
  prefix?: string;
  /** Optional suffix (e.g. ' USD₮') */
  suffix?: string;
  /** Override default suffix className (default: opacity-60 + text-[0.6em]) */
  suffixClassName?: string;
  className?: string;
}

export function LiquidNumber({
  value,
  decimals = 2,
  duration = 800,
  prefix,
  suffix,
  suffixClassName,
  className,
}: LiquidNumberProps) {
  const targetValue = Number(value);
  const [display, setDisplay] = useState(targetValue);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    if (!Number.isFinite(targetValue)) return;
    const startValue = display;
    const startTime = performance.now();
    const delta = targetValue - startValue;
    if (Math.abs(delta) < 1e-9) return;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      // Ease out expo for a smooth deceleration
      const eased = 1 - Math.pow(1 - t, 4);
      setDisplay(startValue + delta * eased);
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(targetValue);
      }
    };

    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetValue, duration]);

  return (
    <span className={cn('lg-numeric inline-flex items-baseline tabular-nums', className)}>
      {prefix && <span className="opacity-60 mr-0.5">{prefix}</span>}
      {formatUsdt(display, decimals)}
      {suffix && <span className={suffixClassName ?? 'opacity-60 ml-1 text-[0.6em]'}>{suffix}</span>}
    </span>
  );
}
